import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { XCircle, RefreshCw, LifeBuoy, Home } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentFailurePage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  // Prefer error details echoed in the URL from the Razorpay handler; fall
  // back to what the backend has logged if the URL params were dropped.
  const urlReason = searchParams.get('reason');
  const urlCode = searchParams.get('code');
  const planIdParam = searchParams.get('plan_id');
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(!!orderId);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/payment/status/${orderId}`);
        if (alive) setStatus(res.data);
      } catch (_) {
        // Silent — we still render a helpful failure page from URL params
        if (process.env.NODE_ENV !== 'production') console.warn('[PaymentFailure] status fetch failed:', _);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner message="Loading payment details…" />
        </div>
      </Layout>
    );
  }

  const reason = urlReason || status?.error_message || 'The payment could not be completed.';
  const code = urlCode || status?.error_code;
  const planName = status?.plan_name;
  const retryPlanId = planIdParam || status?.plan_id;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-8 space-y-6">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100">
            <XCircle className="w-12 h-12 text-red-600" data-testid="payment-failed-icon" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Payment Failed</h1>
          <p className="text-slate-600 max-w-md mx-auto">
            No charge has been made to your card. You can retry the payment or reach out to us for help.
          </p>
        </div>

        <Card className="border-2 border-red-100 bg-red-50/30">
          <CardContent className="p-6 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Reason</p>
              <p className="text-sm text-slate-900" data-testid="payment-failed-reason">
                {reason}
              </p>
            </div>
            {code ? (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Error code</p>
                <p className="text-sm font-mono text-slate-700" data-testid="payment-failed-code">{code}</p>
              </div>
            ) : null}
            {planName ? (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Plan</p>
                <p className="text-sm text-slate-900">{planName}</p>
              </div>
            ) : null}
            {orderId ? (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Order ID</p>
                <p className="text-sm font-mono text-slate-700 break-all">{orderId}</p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="grid sm:grid-cols-3 gap-3">
          <Button
            variant="outline"
            className="h-auto py-4 flex-col gap-1"
            onClick={() => navigate('/dashboard')}
            data-testid="payment-failed-dashboard-btn"
          >
            <Home className="w-5 h-5 text-slate-600" />
            <span className="text-sm">Dashboard</span>
          </Button>
          <Link to="/contact-us" className="block">
            <Button
              variant="outline"
              className="w-full h-auto py-4 flex-col gap-1"
              data-testid="payment-failed-support-btn"
            >
              <LifeBuoy className="w-5 h-5 text-slate-600" />
              <span className="text-sm">Contact Support</span>
            </Button>
          </Link>
          <Button
            className="h-auto py-4 flex-col gap-1 bg-[#0A7EA4] hover:bg-[#08678A]"
            onClick={() => navigate(retryPlanId ? `/checkout/${retryPlanId}` : '/pricing')}
            data-testid="payment-failed-retry-btn"
          >
            <RefreshCw className="w-5 h-5" />
            <span className="text-sm text-white">Try Again</span>
          </Button>
        </div>

        <p className="text-xs text-center text-slate-500">
          If money was debited but the plan didn&apos;t activate, it will be auto-refunded within 5–7 business days.
          Reach out with the Order ID above if you need help.
        </p>
      </div>
    </Layout>
  );
};

export default PaymentFailurePage;
